import { useState, useEffect } from 'react';

interface ClientEvent {
  id: number;
  title: string;
  occasion: string;
  event_date: string;
  public_token: string | null;
  public_token_expires: string | null;
  hasActiveGiftList: boolean;
}

interface ClientGiftIdeasProps {
  cachedData?: ClientEvent[];
  onDataLoaded?: (data: ClientEvent[]) => void;
}

export function ClientGiftIdeas({ cachedData, onDataLoaded }: ClientGiftIdeasProps) {
  const [events, setEvents] = useState<ClientEvent[]>(cachedData || []);
  const [isLoading, setIsLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cachedData) {
      setEvents(cachedData);
      setIsLoading(false);
    } else {
      fetchEvents();
    }
  }, [cachedData]);

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/customer/events');
      const result = await response.json();

      if (result.success) {
        setEvents(result.data);
        onDataLoaded?.(result.data);
      } else {
        setError(result.error || 'Failed to fetch events');
        onDataLoaded?.([]);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      onDataLoaded?.([]);
      console.error('Events fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchEvents}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">My Gift Ideas</h3>
        <p className="text-gray-600">
          View all the gift recommendations that we have created for you
        </p>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-8">
          {/* <div className="text-gray-400 mb-2">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div> */}
          <p className="text-gray-500">No gift recommendations yet</p>
          {/* <p className="text-sm text-gray-400 mt-1">
            Complete a gift flow to see your recommendations here
          </p> */}
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div 
              key={event.id}
              className="border border-gray-200 rounded-lg p-6 bg-white hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-purple-700 mb-2">
                    {event.title || event.occasion}
                  </h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><strong>Event Date:</strong> {formatDate(event.event_date)}</p>
                  </div>
                </div>
                
                <div className="ml-4">
                  {event.hasActiveGiftList && event.public_token ? (
                    <a
                      href={`/gift-list/${event.public_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Gift List
                    </a>
                  ) : (
                    <span className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-lg">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      In Progress
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}